<template>
<div>
    <h2><i class="fa fa-fw fa-lg fa-bookmark-o"></i> {{ $label('operator.company_club') }}</h2>
    <p></p>
    <div id="operator-company" class="v-form-partial">
        <div class="v-form">
            <fieldset>
                <div class="row">
                    <!-- BEGIN Name -->
                    <section class="col col-12">
                        <label class="label">{{ $label('operator.name_label') }}</label>

                        <label class="textarea">
                            <textarea v-model="operator.name"
                                :class="{ 'text-input-error': hasError('name') }"
                                :placeholder="$label('operator.name_placeholder')"
                                :disabled="updating"
                                rows="2">
                            </textarea>
                        </label>

                        <p v-if="hasError('name')" class="text-danger">{{ getError('name') }}</p>
                    </section>
                    <!-- END Name -->
                </div>

                <div class="row">
                    <!-- BEGIN UID -->
                    <section class="col col-6">
                        <label class="label">{{ $label('operator.uid_number_label') }}</label>

                        <label class="input">
                            <input v-model="operator.fiscal_details.uid"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('fiscal_details.uid') }"
                                :placeholder="$label('operator.uid_number_placeholder')"
                                :disabled="updating"
                                type="text"
                                maxlength="15"
                            />
                        </label>

                        <p v-if="hasError('fiscal_details.uid')" class="text-danger">{{ getError('fiscal_details.uid') }}</p>
                    </section>
                    <!-- END UID -->

                    <!-- BEGIN FN -->
                    <section class="col col-6">
                        <label class="label">{{ $label('operator.fn_number_label') }}</label>

                        <label class="input">
                            <input v-model="operator.fiscal_details.fn"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('fiscal_details.fn') }"
                                :placeholder="$label('operator.fn_number_placeholder')"
                                :disabled="updating"
                                type="text"
                                maxlength="11"
                            />
                        </label>

                        <p v-if="hasError('fiscal_details.fn')" class="text-danger">{{ getError('fiscal_details.fn') }}</p>
                    </section>
                    <!-- END FN -->
                </div>

                <div class="row">
                    <!-- BEGIN ZVR -->
                    <section class="col col-6">
                        <label class="label">{{ $label('operator.zvr_number_label') }}</label>

                        <label class="input">
                            <input v-model="operator.fiscal_details.zvr"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('fiscal_details.zvr') }"
                                :placeholder="$label('operator.zvr_number_placeholder')"
                                :disabled="updating"
                                type="text"
                                maxlength="9"
                            />
                        </label>

                        <span v-if="hasError('fiscal_details.zvr')"></span>
                    </section>
                    <!-- END ZVR -->

                    <!-- BEGIN DVR -->
                    <section class="col col-6">
                        <label class="label">{{ $label('operator.dvr_number_label') }}</label>

                        <label class="input">
                            <input v-model="operator.fiscal_details.dvr"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('fiscal_details.dvr') }"
                                :placeholder="$label('operator.dvr_number_placeholder')"
                                :disabled="updating"
                                type="text"
                                maxlength="7"
                            />
                        </label>

                        <span v-if="hasError('fiscal_details.dvr')" class="text-danger">{{ getError('fiscal_details.dvr') }}</span>
                    </section>
                    <!-- END DVR -->
                </div>

                <div class="row">
                    <!-- BEGIN Email address -->
                    <section class="col col-12">
                        <label class="label">{{ $label('common.email_address_long') }}</label>

                        <label class="input">
                            <i class="icon-append icon-envelope"></i>
                            <input v-model="operator.email"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('email') }"
                                :placeholder="$label('common.email_address_short')"
                                :disabled="updating"
                                type="email"
                            />
                        </label>

                        <span class="text-danger">{{ getError('email') }}</span>
                    </section>
                    <!-- END Email address -->
                </div>

                <phone-number resource-type="operators" :resource-id="operatorId" :updating="updating"></phone-number>

                <fax-number resource-type="operators" :resource-id="operatorId" :updating="updating" component-parent-view="operatorBaseDetails"></fax-number>

                <div class="row">
                    <!-- BEGIN Website -->
                    <section class="col col-12">
                        <label class="label">{{ $label('operator.homepage') }}</label>

                        <label class="input">
                            <i class="icon-append icon-globe"></i>
                            <input v-model="operator.website"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('website') }"
                                :disabled="updating"
                                type="text"
                                placeholder="www."
                            />
                        </label>

                        <p class="text-danger">{{ getError('entity_details.www') }}</p>
                    </section>
                    <!-- END Website -->
                </div>
            </fieldset>
        </div>
    </div>
</div>
</template>

<script>
import Api from '@/api';
import FormErrorsMixin from '@/mixins/FormErrors.js';
import ModelActionsMixin from '@/mixins/ModelActions.js';

import PhoneNumber from '@/components/Operator/BaseData/PhoneNumber.vue';
import FaxNumber from '@/components/Common/FaxNumber.vue';

export default {
    props: ['operatorId', 'updating'],

    mixins: [FormErrorsMixin, ModelActionsMixin],

    components: {
        PhoneNumber,
        FaxNumber,
    },

    data() {
        return {
            validatedInputs: [
                'name', 'email', 'website',
                'fiscal_details.uid', 'fiscal_details.fn',
                'fiscal_details.zvr', 'fiscal_details.dvr',
            ],
            errors: [],
            parentView: 'operatorBaseDetails',
            modelData: 'operator',
            alwaysUpdate: true,
            operator: {
                id: null,
                name: null,
                email: null,
                website: null,
                fiscal_details: {
                    uid: '',
                    zvr: '',
                    dvr: '',
                    fn: '',
                }
            },
        }
    },

    mounted() {
        if (this.operatorId != null) {
            this.initializeCompanyDetails();
        }
    },

    methods: {
        initializeCompanyDetails() {
            this.getOperator();
        },

        getOperator() {
            let endpoint = this.$partnerEndpoint('operators/' + this.operatorId);
            Api().get(endpoint)
                .then((response) => {
                    this.operator = response.data.data;
                    this.emitModelLoadedEvent();
                })
                .catch((error) => {
                    this.emitModelLoadedEvent(false);
                });
        },

        updateModel() {
            this.resetErrors();
            let endpoint = this.$partnerEndpoint('operators/' + this.operatorId);

            Api().put(endpoint, this.operator)
                .then((response) => {
                    this.emitModelUpdatedEvent();
                })
                .catch((error) => {
                    this.emitModelUpdatedEvent(false);
                    this.storeErrors(error.response.data.errors);
                });
        },
    },

    watch: {
        operatorId(operatorId) {
            if (operatorId) {
                this.initializeCompanyDetails();
            }
        },

        validationErrors(errors) {
            if (Object.entries(errors).length === 0 && errors.constructor === Array) {
                this.resetErrors();
            } else {
                this.storeErrors(errors);
            }
        },
    },
}
</script>
